import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Plus, Edit, Trash2, Key, Search } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Users() {
  const { user: currentUser } = useAuth();
  const isSuper = currentUser?.role === 'super_admin';
  const [users, setUsers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    password: '',
    role: 'employee',
    branch_id: ''
  });
  const [editFormData, setEditFormData] = useState({
    name: '',
    phone: '',
    role: '',
    branch_id: ''
  });
  const [newPassword, setNewPassword] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [usersRes, branchesRes] = await Promise.all([
        axios.get(`${API_URL}/api/users`),
        axios.get(`${API_URL}/api/branches`),
      ]);
      setUsers(usersRes.data);
      setBranches(branchesRes.data);
    } catch (error) {
      toast.error('فشل تحميل البيانات');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const payload = { ...formData };
      // Strip empty branch_id (let backend default for non-super); for super → require it
      if (!payload.branch_id) delete payload.branch_id;
      await axios.post(`${API_URL}/api/auth/register`, payload);
      toast.success('تم إضافة المستخدم بنجاح');

      setOpen(false);
      setFormData({
        name: '',
        phone: '',
        password: '',
        role: 'employee',
        branch_id: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل إضافة المستخدم');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا المستخدم؟')) return;
    
    try {
      await axios.delete(`${API_URL}/api/users/${id}`);
      toast.success('تم حذف المستخدم بنجاح');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل حذف المستخدم');
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('كلمة المرور يجب أن تكون 6 أحرف على الأقل');
      return;
    }

    try {
      await axios.put(`${API_URL}/api/users/${selectedUser.id}/reset-password`, {
        new_password: newPassword
      });
      toast.success('تم تحديث كلمة المرور بنجاح');
      setPasswordDialogOpen(false);
      setNewPassword('');
      setSelectedUser(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل تحديث كلمة المرور');
    }
  };

  const openEditDialog = (user) => {
    setSelectedUser(user);
    setEditFormData({
      name: user.name,
      phone: user.phone,
      role: user.role,
      branch_id: user.branch_id || ''
    });
    setEditOpen(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const payload = { ...editFormData };
      // Only super_admin can change branch; strip empty / super_admin role
      if (!isSuper || payload.role === 'super_admin' || !payload.branch_id) {
        delete payload.branch_id;
      }
      await axios.put(`${API_URL}/api/users/${selectedUser.id}`, payload);
      toast.success('تم تحديث بيانات المستخدم بنجاح');
      setEditOpen(false);
      setSelectedUser(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل تحديث المستخدم');
    }
  };

  const openPasswordDialog = (user) => {
    setSelectedUser(user);
    setNewPassword('');
    setPasswordDialogOpen(true);
  };

  const branchMap = React.useMemo(() => {
    const m = {};
    branches.forEach((b) => { m[b.id] = b.name; });
    return m;
  }, [branches]);

  // ── Search/filter (super_admin only) ────────────────
  const [searchName, setSearchName] = useState('');
  const [filterBranch, setFilterBranch] = useState('all');

  const filteredUsers = React.useMemo(() => {
    if (!isSuper) return users;
    const q = searchName.trim().toLowerCase();
    return users.filter((u) => {
      if (filterBranch !== 'all' && u.branch_id !== filterBranch) return false;
      if (q && !(u.name || '').toLowerCase().includes(q) && !(u.phone || '').includes(q)) return false;
      return true;
    });
  }, [users, searchName, filterBranch, isSuper]);

  if (loading) {
    return <div className="text-center py-12">جاري التحميل...</div>;
  }

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-black text-primary">إدارة المستخدمين</h1>
          <p className="text-muted-foreground mt-1">إنشاء حسابات وإدارة صلاحيات المستخدمين</p>
        </div>
        <Dialog open={open} onOpenChange={(isOpen) => {
          setOpen(isOpen);
          if (!isOpen) {
            setFormData({
              name: '',
              phone: '',
              password: '',
              role: 'employee'
            });
          }
        }}>
          <DialogTrigger asChild>
            <Button data-testid="add-user-button" className="gap-2">
              <Plus className="h-4 w-4" />
              إضافة مستخدم
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[500px]" dir="rtl">
            <DialogHeader>
              <DialogTitle>إضافة مستخدم جديد</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">الاسم</Label>
                <Input
                  id="name"
                  data-testid="user-name-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="text-right"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">رقم الجوال</Label>
                <Input
                  id="phone"
                  data-testid="user-phone-input"
                  type="tel"
                  placeholder="05xxxxxxxx"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="text-right"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">كلمة المرور</Label>
                <Input
                  id="password"
                  data-testid="user-password-input"
                  type="password"
                  placeholder="6 أحرف على الأقل"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="text-right"
                  required
                  minLength={6}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="role">الصلاحية</Label>
                <Select
                  value={formData.role}
                  onValueChange={(value) => {
                    setFormData({ ...formData, role: value });
                  }}
                >
                  <SelectTrigger data-testid="role-select" className="text-right">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {isSuper && <SelectItem value="super_admin">مدير عام</SelectItem>}
                    <SelectItem value="admin">مدير</SelectItem>
                    <SelectItem value="supervisor">مشرف</SelectItem>
                    <SelectItem value="employee">موظف</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {isSuper && formData.role !== 'super_admin' && (
                <div className="space-y-2">
                  <Label>الفرع</Label>
                  <Select
                    value={formData.branch_id || ''}
                    onValueChange={(v) => setFormData({ ...formData, branch_id: v })}
                  >
                    <SelectTrigger data-testid="user-branch-select" className="text-right">
                      <SelectValue placeholder="اختر الفرع" />
                    </SelectTrigger>
                    <SelectContent>
                      {branches.map((b) => (
                        <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="flex gap-3 justify-end">
                <Button type="button" variant="ghost" onClick={() => setOpen(false)}>
                  إلغاء
                </Button>
                <Button type="submit" data-testid="save-user-button">
                  حفظ
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="border-border">
        {isSuper && (
          <div className="p-4 border-b border-border bg-accent/20 flex flex-col md:flex-row gap-3" data-testid="users-search-bar">
            <div className="flex-1 relative">
              <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="ابحث باسم المستخدم أو رقم الجوال..."
                value={searchName}
                onChange={(e) => setSearchName(e.target.value)}
                className="text-right pr-9"
                data-testid="users-search-input"
              />
            </div>
            <div className="w-full md:w-64">
              <Select value={filterBranch} onValueChange={setFilterBranch}>
                <SelectTrigger className="text-right" data-testid="users-filter-branch">
                  <SelectValue placeholder="كل الفروع" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">— كل الفروع —</SelectItem>
                  {branches.map((b) => (
                    <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center text-sm text-muted-foreground font-numbers" data-testid="users-result-count">
              {filteredUsers.length} / {users.length}
            </div>
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-accent/50">
              <tr>
                <th className="text-right py-4 px-6 text-sm font-bold text-foreground">الاسم</th>
                <th className="text-right py-4 px-6 text-sm font-bold text-foreground">رقم الجوال</th>
                <th className="text-right py-4 px-6 text-sm font-bold text-foreground">الصلاحية</th>
                {isSuper && (
                  <th className="text-right py-4 px-6 text-sm font-bold text-foreground">الفرع</th>
                )}
                <th className="text-right py-4 px-6 text-sm font-bold text-foreground">الإجراءات</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan={isSuper ? 5 : 4} className="text-center py-12 text-muted-foreground">
                    لا توجد نتائج مطابقة
                  </td>
                </tr>
              ) : (
                filteredUsers.map((user) => (
                  <tr key={user.id} className="border-t border-border hover:bg-accent/50 transition-colors">
                    <td className="py-4 px-6 text-foreground">{user.name}</td>
                    <td className="py-4 px-6 text-foreground font-numbers">{user.phone}</td>
                    <td className="py-4 px-6">
                      <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                        user.role === 'super_admin'
                          ? 'bg-purple-100 text-purple-700'
                          : user.role === 'admin' 
                            ? 'bg-brand_blue/10 text-brand_blue' 
                            : user.role === 'supervisor'
                              ? 'bg-amber-100 text-amber-700'
                              : 'bg-success/10 text-success'
                      }`}>
                        {user.role === 'super_admin' ? 'مدير عام' : user.role === 'admin' ? 'مدير' : user.role === 'supervisor' ? 'مشرف' : 'موظف'}
                      </span>
                    </td>
                    {isSuper && (
                      <td className="py-4 px-6" data-testid={`user-branch-${user.id}`}>
                        {user.role === 'super_admin' ? (
                          <span className="text-xs text-foreground/50">— كل الفروع —</span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-brand_blue/5 text-brand_blue text-xs font-bold">
                            {branchMap[user.branch_id] || '—'}
                          </span>
                        )}
                      </td>
                    )}
                    <td className="py-4 px-6">
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDialog(user)}
                          data-testid={`edit-user-${user.id}`}
                          className="h-8 w-8 p-0"
                          title="تعديل البيانات"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openPasswordDialog(user)}
                          data-testid={`reset-password-${user.id}`}
                          className="h-8 w-8 p-0"
                          title="تغيير كلمة المرور"
                        >
                          <Key className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(user.id)}
                          data-testid={`delete-user-${user.id}`}
                          className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Edit User Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-[500px]" dir="rtl">
          <DialogHeader>
            <DialogTitle>تعديل بيانات المستخدم</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-name">الاسم</Label>
              <Input
                id="edit-name"
                data-testid="edit-user-name-input"
                value={editFormData.name}
                onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                className="text-right"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-phone">رقم الجوال</Label>
              <Input
                id="edit-phone"
                data-testid="edit-user-phone-input"
                type="tel"
                placeholder="05xxxxxxxx"
                value={editFormData.phone}
                onChange={(e) => setEditFormData({ ...editFormData, phone: e.target.value })}
                className="text-right"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-role">الصلاحية</Label>
              <Select
                value={editFormData.role}
                onValueChange={(value) => {
                  setEditFormData({ ...editFormData, role: value });
                }}
              >
                <SelectTrigger data-testid="edit-role-select" className="text-right">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {isSuper && <SelectItem value="super_admin">مدير عام</SelectItem>}
                  <SelectItem value="admin">مدير</SelectItem>
                  <SelectItem value="supervisor">مشرف</SelectItem>
                  <SelectItem value="employee">موظف</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {isSuper && editFormData.role !== 'super_admin' && (
              <div className="space-y-2">
                <Label>الفرع</Label>
                <Select
                  value={editFormData.branch_id || ''}
                  onValueChange={(v) => setEditFormData({ ...editFormData, branch_id: v })}
                >
                  <SelectTrigger data-testid="edit-user-branch-select" className="text-right">
                    <SelectValue placeholder="اختر الفرع" />
                  </SelectTrigger>
                  <SelectContent>
                    {branches.map((b) => (
                      <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  ملاحظة: المبيعات القديمة تبقى على فرعها الأصلي، والمبيعات الجديدة تُسجَّل على الفرع الجديد.
                </p>
              </div>
            )}

            <div className="flex gap-3 justify-end">
              <Button type="button" variant="ghost" onClick={() => setEditOpen(false)}>
                إلغاء
              </Button>
              <Button type="submit" data-testid="confirm-edit-user-button">
                تحديث
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Password Reset Dialog */}
      <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
        <DialogContent className="sm:max-w-[400px]" dir="rtl">
          <DialogHeader>
            <DialogTitle>تغيير كلمة المرور</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>المستخدم</Label>
              <Input
                value={selectedUser?.name || ''}
                disabled
                className="text-right bg-accent"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-password">كلمة المرور الجديدة</Label>
              <Input
                id="new-password"
                data-testid="new-password-input"
                type="password"
                placeholder="6 أحرف على الأقل"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="text-right"
                minLength={6}
              />
            </div>
            <div className="flex gap-3 justify-end">
              <Button type="button" variant="ghost" onClick={() => setPasswordDialogOpen(false)}>
                إلغاء
              </Button>
              <Button
                onClick={handleResetPassword}
                data-testid="confirm-reset-password-button"
              >
                تحديث
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
